import { call, put } from 'redux-saga/effects';
import { get } from 'ravenjs/utils/lodash';

import {
    createAISessionAPI,
    sendMessageAPI,
    getAnswerAPI,
    createFeedbackAPI,
    closeAISessionAPI,
    sendEmailCloseAISession,
    getHistoryChatAPI,
    getChatSessionDetailsAPI,
    getChatSessionsByIdAPI,
    getChatHistoryPaginatedAPI,
    submitAiAssistantMessageAPI,
    saveAiAssistantFeedbackAPI,
    getAiAssistantFeedbackAPI,
    getAiAssistantHealthAPI,
    getAiAssistantSuggestionsAPI,
    saveAiPolicyAcceptanceAPI,
    getAiAssistantFaqsAPI,
    getAiAssistantChatsAPI,
    getAiAssistantChatByIdAPI,
    createAiAssistantChatAPI,
    continueAiAssistantChatAPI,
    exportAiAssistantConversationAPI,
    renameAiAssistantChatAPI,
    deleteAiAssistantChatAPI,
} from 'modules/apis';
import * as aiAssistantActions from '../actions';

/**
 * Function for create session
 *
 * @method createAISessionSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} location
 */
export function* createAISessionSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(createAISessionAPI, params);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.createAISession.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.createAISession.error(error));
        return error;
    }
}

/**
 * Function for send message
 *
 * @method sendMessageSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} location
 */
export function* sendMessageSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(sendMessageAPI, params);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.sendMessage.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.sendMessage.error(error));
        return error;
    }
}

/**
 * Function for get answer
 *
 * @method getAnswerSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} location
 */
export function* getAnswerSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(getAnswerAPI, params);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.getAnswer.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.getAnswer.error(error));
        return error;
    }
}

/**
 * Function for create feedback
 *
 * @method createFeedbackSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} location
 */
export function* createFeedbackSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(createFeedbackAPI, params);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.createFeedback.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.createFeedback.error(error));
        return error;
    }
}

/**
 * Submit a prompt to the JAPI AI Assistant, starting or continuing a conversation. Single
 * request/response, no polling. The rejected action promise drives the tray's error state.
 *
 * @method submitAiAssistantMessageSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} The `AiAssistantMessageResponseDTO`
 */
export function* submitAiAssistantMessageSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(submitAiAssistantMessageAPI, params);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.submitAiAssistantMessage.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.submitAiAssistantMessage.error(error));
        return error;
    }
}

/**
 * Save like/dislike/none feedback for one answer.
 *
 * @method saveAiAssistantFeedbackSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} The `AiAssistantFeedbackResponseDTO`
 */
export function* saveAiAssistantFeedbackSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(saveAiAssistantFeedbackAPI, params);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.saveAiAssistantFeedback.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.saveAiAssistantFeedback.error(error));
        return error;
    }
}

/**
 * Read the current feedback for one answer.
 *
 * @method getAiAssistantFeedbackSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} The `AiAssistantFeedbackResponseDTO`
 */
export function* getAiAssistantFeedbackSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(getAiAssistantFeedbackAPI, params);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.getAiAssistantFeedback.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.getAiAssistantFeedback.error(error));
        return error;
    }
}

/**
 * Read upstream availability.
 *
 * @method getAiAssistantHealthSaga
 * @type   {Generator}
 * @return {Object} The `AiAssistantHealthResponseDTO`
 */
export function* getAiAssistantHealthSaga() {
    try {
        const response = yield call(getAiAssistantHealthAPI);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.getAiAssistantHealth.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.getAiAssistantHealth.error(error));
        return error;
    }
}

/**
 * Read the frequently asked questions shown as suggestion chips.
 *
 * @method getAiAssistantSuggestionsSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} The suggestions payload
 */
export function* getAiAssistantSuggestionsSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(getAiAssistantSuggestionsAPI, params.userId);
        const results = get(response, 'data', []);
        yield put(aiAssistantActions.getAiAssistantSuggestions.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.getAiAssistantSuggestions.error(error));
        return error;
    }
}

/**
 * Function for close session
 *
 * @method closeAISessionSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} location
 */
export function* closeAISessionSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(closeAISessionAPI, params.chatSessionId);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.closeAISession.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.closeAISession.error(error));
        return error;
    }
}

/**
 * Function for send email
 *
 * @method sendEmailCloseAISessionSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} location
 */
export function* sendEmailCloseAISessionSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(sendEmailCloseAISession, params);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.sendEmailCloseAISession.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.sendEmailCloseAISession.error(error));
        return error;
    }
}

/**
 * Function for history chat
 *
 * @method getHistoryChatSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} location
 */
export function* getHistoryChatSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(getHistoryChatAPI, params.chatSessionId);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.getHistoryChat.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.getHistoryChat.error(error));
        return error;
    }
}

/**
 * Saga for chat session details
 *
 * @method getChatSessionDetailsSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} location
 */
export function* getChatSessionDetailsSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(getChatSessionDetailsAPI, params.chatSessionId);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.getChatSessionDetails.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.getChatSessionDetails.error(error));
        return error;
    }
}

/**
 * Saga for getting chat sessions by UUID
 *
 * @method getChatSessionsByIdSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} results
 */
export function* getChatSessionsByIdSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(getChatSessionsByIdAPI, params.logId);
        const results = get(response, 'data', []);
        yield put(aiAssistantActions.getChatSessionsById.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.getChatSessionsById.error(error));
        return error;
    }
}

/**
 * Saga for getting paginated chat history by userId
 *
 * @method getChatHistoryPaginatedSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} results
 */
export function* getChatHistoryPaginatedSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(getChatHistoryPaginatedAPI, params);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.getChatHistoryPaginated.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.getChatHistoryPaginated.error(error));
        return error;
    }
}

/**
 * Save the user's AI policy acceptance.
 *
 * @method saveAiPolicyAcceptanceSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} The saved acceptance status
 */
export function* saveAiPolicyAcceptanceSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(saveAiPolicyAcceptanceAPI, params.userId);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.saveAiPolicyAcceptance.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.saveAiPolicyAcceptance.error(error));
        return error;
    }
}

/**
 * Get FAQ suggestions for the AI Assistant.
 *
 * @method getAiAssistantFaqsSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} The FAQ suggestions
 */
export function* getAiAssistantFaqsSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(getAiAssistantFaqsAPI, params.userId);
        const results = get(response, 'data', []);
        yield put(aiAssistantActions.getAiAssistantFaqs.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.getAiAssistantFaqs.error(error));
        return error;
    }
}

/**
 * Get all active chats for a user.
 *
 * @method getAiAssistantChatsSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} The list of chats
 */
export function* getAiAssistantChatsSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(getAiAssistantChatsAPI, params.userId);
        const results = get(response, 'data', []);
        yield put(aiAssistantActions.getAiAssistantChats.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.getAiAssistantChats.error(error));
        return error;
    }
}

/**
 * Get a single chat by ID.
 *
 * @method getAiAssistantChatByIdSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} The chat with conversations
 */
export function* getAiAssistantChatByIdSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(getAiAssistantChatByIdAPI, params.userId, params.chatId);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.getAiAssistantChatById.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.getAiAssistantChatById.error(error));
        return error;
    }
}

/**
 * Create a new chat for a user.
 *
 * @method createAiAssistantChatSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} The new chat and assistant response
 */
export function* createAiAssistantChatSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(createAiAssistantChatAPI, params.userId, {
            message: params.message,
        });
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.createAiAssistantChat.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.createAiAssistantChat.error(error));
        return error;
    }
}

/**
 * Continue an existing chat with a new message.
 *
 * @method continueAiAssistantChatSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} The updated conversation and assistant response
 */
export function* continueAiAssistantChatSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(continueAiAssistantChatAPI, params.userId, params.chatId, {
            message: params.message,
        });
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.continueAiAssistantChat.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.continueAiAssistantChat.error(error));
        return error;
    }
}

/**
 * Download the full result set of one answer as CSV.
 *
 * @method exportAiAssistantConversationSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} The export manifest
 */
export function* exportAiAssistantConversationSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(
            exportAiAssistantConversationAPI,
            params.userId,
            params.chatId,
            params.conversationId
        );
        const results = get(response, 'data', null);
        yield put(aiAssistantActions.exportAiAssistantConversation.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.exportAiAssistantConversation.error(error));
        return error;
    }
}

/**
 * Rename a chat.
 *
 * @method renameAiAssistantChatSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} The updated chat
 */
export function* renameAiAssistantChatSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(renameAiAssistantChatAPI, params.userId, params.chatId, {
            chatName: params.chatName,
        });
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.renameAiAssistantChat.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.renameAiAssistantChat.error(error));
        return error;
    }
}

/**
 * Soft delete a chat.
 *
 * @method deleteAiAssistantChatSaga
 * @type   {Generator}
 * @param  {Object} action The redux action
 * @return {Object} The result
 */
export function* deleteAiAssistantChatSaga(action) {
    try {
        const {
            payload: { params },
        } = action;
        const response = yield call(deleteAiAssistantChatAPI, params.userId, params.chatId);
        const results = get(response, 'data', {});
        yield put(aiAssistantActions.deleteAiAssistantChat.success(results));
        return results;
    } catch (error) {
        yield put(aiAssistantActions.deleteAiAssistantChat.error(error));
        return error;
    }
}
